/**

 * This file is used to cache product pricing and quantity discounts
 */

define('modules/Product/CachedPrice',[
    'jquery',
    'global/all',
    'plugins/accounting.min'
], function ($, Roc) {
    'use strict';

    // contains cached pricing per product id.
    // pricing contains the prices and quantity discounts table.
    var _productPricingCache = [];

    function updateCachedPricing(productId, productPricing) {
        _productPricingCache[productId] = productPricing;
    }

    /**
     * Retrieves cached pricing for the provided productId
     * IMPORTANT: This returns a copy of the cached pricing object, so that data manipulations can be done on it without affecting the cached prices.
     * 
     * @param {any} productId for which to retrieve cached pricing
     * @returns {any} cloned, matching pricing record, or null, if pricing record is not found
     */
    function getPricingFromCache(productId) {
        var pricing = _productPricingCache[productId];

        if (!pricing) {
            return null;
        }

        return $.extend({}, pricing);
    }

    /**
     * Retrieves quantity discounts from cached pricing for given product
     *
     * @param {string} productId - required, productId for which we want to retrieve the discounts
     * @returns {object} quantity discounts for the given product, or null
     */
    function getQuantityDiscounts(productId) {
        var pricing = getPricingFromCache(productId);
        return getQuantityDiscountsFromPricing(pricing);
    }

    function getQuantityDiscountsFromPricing(pricing) {
        var hasQuantityDiscounts = pricing && pricing.customFields && pricing.customFields["quantityDiscounts"];

        if (hasQuantityDiscounts) {
            return pricing.customFields["quantityDiscounts"];
        }
        return null;
    }

    function isDiscountedProduct(productId) {
        var pricing = getPricingFromCache(productId);
        if (pricing && pricing.customFields && pricing.customFields["isDiscounted"]) {
            return true;
        }
        return false;
    }

    /**
     * Given the productId and quantity retrieves the pricing record, in which the userUnitPrice field
     * is populated with the unit price for the matching price break.
     *
     * If the quantity is lower than the min qty of the first price break, it just returns an unchanged pricing record.
     *
     * @param {string} productId - required, productId for which we want to retrieve the pricing
     * @param {int} quantity - required, productId for which we want to retrieve the discounts
     * @returns {object} The product pricing record, in which the userUnitPrice field is populated with the unit price for the matching price break.
     */
    function getQuantityDiscountedPricing(productId, quantity) {
        if (!quantity) {
            quantity = 1;
        }
        var pricing = getPricingFromCache(productId);

        if (!pricing) {
            return null;
        }

        var quantityDiscounts = getQuantityDiscountsFromPricing(pricing);

        if (quantityDiscounts && quantityDiscounts.length > 0) {
            for (var i = 0; i < quantityDiscounts.length; i++) {
                var currentBreak = quantityDiscounts[i];

                // return the base price, if quantity is less than the first bracket
                if (quantity < currentBreak.qty) {
                    return pricing;
                }

                // NOTE: we only need to update the userUnitPrice when we are using a quantityBreak
                if (quantity >= currentBreak.qty) {
                    pricing.userUnitPrice = currentBreak.unitPrice;
                }
            }
        }

        return pricing;
    }

    /**
     * Initializes this cache of product pricing for the listing display.
     * This creates a pricing cache entry for each product in the listing.
     * The pricing records are in the default unit for each product.
     *
     * @param {any} data - a list of products for which to cache pricing
     */
    function initForProductList(data) {
        if (data && data.items) {
            for (var i = 0; i < data.items.length; i++) {
                var item = data.items[i];

                var hasPrice = item &&
                    item.product &&
                    item.product.productPricing;

                if (hasPrice) {
                    updateCachedPricing(item.product.id, item.product.productPricing);
                }
            }
        }
    }

    /**
     * Initializes this cache for a single product (details page)
     * @param {any} productId - the productId displayed on the PDP
     * @param {any} productPricing - product pricing in a currently selected unit
     */
    function initForDetailsPage(productId, productPricing) {
        updateCachedPricing(productId, productPricing);
    }

    var CachedPrice = {
        initForDetailsPage: initForDetailsPage,
        initForProductList: initForProductList,
        updateCachedPricing: updateCachedPricing,
        getQuantityDiscounts: getQuantityDiscounts,
        getQuantityDiscountedPricing: getQuantityDiscountedPricing,
        isDiscountedProduct: isDiscountedProduct
    };

    Roc.Logger.debug('CachedPrice.js Loaded:', CachedPrice);

    return CachedPrice;
});
